import React, { useState, useMemo, useEffect } from "react";
import { IoIosArrowDropdown } from "react-icons/io";
import { RiResetLeftLine } from "react-icons/ri";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const UpdateFilters = ({ data, setRecords, openStates, setOpenStates }) => {
  // State for search inputs
  const [searchValues, setSearchValues] = useState({
    Section: "",
    Department: "",
    Type: "",
    "Block Type": "",
    "Granted/Not": "",
    "Availed/Not": "",
    Burst: "",
    Corridore: "",
    Direction: "",
    Rolling_Block: "",
  });

  // State for selected filters
  const [selectedSection, setSelectedSection] = useState(new Set());
  const [selectedDepartment, setSelectedDepartment] = useState(new Set());
  const [selectedType, setSelectedType] = useState(new Set());
  const [selectedBlockType, setSelectedBlockType] = useState(new Set());
  const [selectedGrantedNot, setSelectedGrantedNot] = useState(new Set());
  const [selectedAvailedNot, setSelectedAvailedNot] = useState(new Set());
  const [selectedBurst, setSelectedBurst] = useState(new Set());
  const [selectedCorridore, setSelectedCorridore] = useState(new Set());
  const [selectedDirection, setSelectedDirection] = useState(new Set());
  const [selectedRollingBlock, setSelectedRollingBlock] = useState(new Set());

  const toggleSelection = (setFunc, currentSet, value) => {
    const newSet = new Set(currentSet);
    newSet.has(value) ? newSet.delete(value) : newSet.add(value);
    setFunc(newSet);
  };

  const toggleDropdown = (title) => {
    setOpenStates((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleSearchChange = (title, value) => {
    setSearchValues((prev) => ({
      ...prev,
      [title]: value,
    }));
  };

  // Initialize filter options
  const defaultSetup = () => {
    if (!data?.length) return;

    const allSection = [
      ...new Set(data.map((d) => d.section).filter((v) => v != null)),
    ];
    const allDepartment = [
      ...new Set(data.map((d) => d.Department).filter((v) => v != null)),
    ];
    const allType = [
      ...new Set(data.map((d) => d.Type).filter((v) => v != null)),
    ];
    const allBlockType = [
      ...new Set(data.map((d) => d.Block_Type).filter((v) => v != null)),
    ];
    const allGrantedNot = [
      ...new Set(data.map((d) => d.Granted_Not).filter((v) => v != null)),
    ];
    const allAvailedNot = [
      ...new Set(data.map((d) => d.Availed_Not).filter((v) => v != null)),
    ];
    const allBurst = [
      ...new Set(data.map((d) => d.Burst).filter((v) => v != null)),
    ];
    const allCorridore = [
      ...new Set(data.map((d) => d.Corridore).filter((v) => v != null)),
    ];
    const allDirection = [
      ...new Set(data.map((d) => d.Direction).filter((v) => v != null)),
    ];
    const allRollingBlock = [
      ...new Set(data.map((d) => d.Rolling_Block).filter((v) => v != null)),
    ];

    setSelectedSection(new Set(allSection));
    setSelectedDepartment(new Set(allDepartment));
    setSelectedType(new Set(allType));
    setSelectedBlockType(new Set(allBlockType));
    setSelectedGrantedNot(new Set(allGrantedNot));
    setSelectedAvailedNot(new Set(allAvailedNot));
    setSelectedBurst(new Set(allBurst));
    setSelectedCorridore(new Set(allCorridore));
    setSelectedDirection(new Set(allDirection));
    setSelectedRollingBlock(new Set(allRollingBlock));
    setSearchValues({
      Section: "",
      Department: "",
      Type: "",
      "Block Type": "",
      "Granted/Not": "",
      "Availed/Not": "",
      Burst: "",
      Corridore: "",
      Direction: "",
      Rolling_Block: "",
    });
    setOpenStates({
      Section: false,
      Department: false,
      Type: false,
      "Block Type": false,
      "Granted/Not": false,
      "Availed/Not": false,
      Burst: false,
      Corridore: false,
      Direction: false,
      Rolling_Block: false,
    });
  };

  useEffect(() => {
    defaultSetup();
  }, [data]);

  // Available options for each filter
  const availableSection = useMemo(() => {
    return [...new Set(data?.map((d) => d.section).filter((v) => v != null))];
  }, [data]);

  const availableDirection = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter((entry) => selectedSection.has(entry.section))
          .map((d) => d.Direction)
          .filter((v) => v != null)
      ),
    ];
  }, [data, selectedSection]);

  const availableDepartment = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedSection.has(entry.section) &&
              selectedDirection.has(entry.Direction)
          )
          .map((d) => d.Department)
          .filter((v) => v != null)
      ),
    ];
  }, [data, selectedSection, selectedDirection]);

  const availableType = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedSection.has(entry.section) &&
              selectedDirection.has(entry.Direction) &&
              selectedDepartment.has(entry.Department)
          )
          .map((d) => d.Type)
          .filter((v) => v != null)
      ),
    ];
  }, [data, selectedSection, selectedDirection, selectedDepartment]);

  const availableBlockType = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedSection.has(entry.section) &&
              selectedDirection.has(entry.Direction) &&
              selectedDepartment.has(entry.Department) &&
              selectedType.has(entry.Type)
          )
          .map((d) => d.Block_Type)
          .filter((v) => v != null)
      ),
    ];
  }, [
    data,
    selectedSection,
    selectedDirection,
    selectedDepartment,
    selectedType,
  ]);

  const availableGrantedNot = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedSection.has(entry.section) &&
              selectedDirection.has(entry.Direction) &&
              selectedDepartment.has(entry.Department) &&
              selectedType.has(entry.Type) &&
              selectedBlockType.has(entry.Block_Type)
          )
          .map((d) => d.Granted_Not)
          .filter((v) => v != null)
      ),
    ];
  }, [
    data,
    selectedSection,
    selectedDirection,
    selectedDepartment,
    selectedType,
    selectedBlockType,
  ]);

  const availableAvailedNot = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedSection.has(entry.section) &&
              selectedDirection.has(entry.Direction) &&
              selectedDepartment.has(entry.Department) &&
              selectedType.has(entry.Type) &&
              selectedBlockType.has(entry.Block_Type) &&
              selectedGrantedNot.has(entry.Granted_Not)
          )
          .map((d) => d.Availed_Not)
          .filter((v) => v != null)
      ),
    ];
  }, [
    data,
    selectedSection,
    selectedDirection,
    selectedDepartment,
    selectedType,
    selectedBlockType,
    selectedGrantedNot,
  ]);

  const availableBurst = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedSection.has(entry.section) &&
              selectedDirection.has(entry.Direction) &&
              selectedDepartment.has(entry.Department) &&
              selectedType.has(entry.Type) &&
              selectedBlockType.has(entry.Block_Type) &&
              selectedGrantedNot.has(entry.Granted_Not) &&
              selectedAvailedNot.has(entry.Availed_Not)
          )
          .map((d) => d.Burst)
          .filter((v) => v != null)
      ),
    ];
  }, [
    data,
    selectedSection,
    selectedDirection,
    selectedDepartment,
    selectedType,
    selectedBlockType,
    selectedGrantedNot,
    selectedAvailedNot,
  ]);

  const availableCorridore = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedSection.has(entry.section) &&
              selectedDirection.has(entry.Direction) &&
              selectedDepartment.has(entry.Department) &&
              selectedType.has(entry.Type) &&
              selectedBlockType.has(entry.Block_Type) &&
              selectedGrantedNot.has(entry.Granted_Not) &&
              selectedAvailedNot.has(entry.Availed_Not) &&
              selectedBurst.has(entry.Burst)
          )
          .map((d) => d.Corridore)
          .filter((v) => v != null)
      ),
    ];
  }, [
    data,
    selectedSection,
    selectedDirection,
    selectedDepartment,
    selectedType,
    selectedBlockType,
    selectedGrantedNot,
    selectedAvailedNot,
    selectedBurst,
  ]);

  const availableRollingBlock = useMemo(() => {
    return [
      ...new Set(
        data
          ?.filter(
            (entry) =>
              selectedSection.has(entry.section) &&
              selectedDirection.has(entry.Direction) &&
              selectedDepartment.has(entry.Department) &&
              selectedType.has(entry.Type) &&
              selectedBlockType.has(entry.Block_Type) &&
              selectedGrantedNot.has(entry.Granted_Not) &&
              selectedAvailedNot.has(entry.Availed_Not) &&
              selectedBurst.has(entry.Burst) &&
              selectedCorridore.has(entry.Corridore)
          )
          .map((d) => d.Rolling_Block)
          .filter((v) => v != null)
      ),
    ];
  }, [
    data,
    selectedSection,
    selectedDirection,
    selectedDepartment,
    selectedType,
    selectedBlockType,
    selectedGrantedNot,
    selectedAvailedNot,
    selectedBurst,
    selectedCorridore,
  ]);

  // Filtered data based on selections
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data.filter((entry) => {
      return (
        selectedSection.has(entry.section) &&
        selectedDepartment.has(entry.Department) &&
        selectedType.has(entry.Type) &&
        selectedBlockType.has(entry.Block_Type) &&
        selectedGrantedNot.has(entry.Granted_Not) &&
        selectedAvailedNot.has(entry.Availed_Not) &&
        selectedBurst.has(entry.Burst) &&
        selectedCorridore.has(entry.Corridore) &&
        selectedDirection.has(entry.Direction) &&
        selectedRollingBlock.has(entry.Rolling_Block)
      );
    });
  }, [
    data,
    selectedSection,
    selectedDepartment,
    selectedType,
    selectedBlockType,
    selectedGrantedNot,
    selectedAvailedNot,
    selectedBurst,
    selectedCorridore,
    selectedDirection,
    selectedRollingBlock,
  ]);

  // Update parent component with filtered data
  useEffect(() => {
    setRecords(filteredData);
  }, [filteredData, setRecords]);

  const renderCheckboxList = (title, items, selectedSet, setFunc) => {
    const filteredItems = items.filter((item) =>
      item
        ?.toString()
        ?.toLowerCase()
        ?.includes(searchValues[title]?.toLowerCase() || "")
    );

    return (
      <div className="col-sm mt-2 p-1">
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-light"
          style={{
            position: "relative",
            fontSize: 12,
            borderRadius: "5px",
          }}
        >
          <div
            className="papaDiv"
            onClick={() => toggleDropdown(title)}
            style={{
              border: "1px solid #ccc",
              padding: "8px",
              cursor: "pointer",
              minWidth: "150px",
              maxHeight: "50px",
              overflow: "hidden",
              userSelect: "none",
            }}
          >
            Select {title} {"  "}
            <IoIosArrowDropdown
              style={{
                fontSize: 15,
                transition: "transform 0.3s ease",
                transform: openStates[title]
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </div>
          {openStates[title] && (
            <div
              className="papaDiv"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                border: "1px solid #ccc",
                background: "#fff",
                zIndex: 10,
              }}
            >
              <strong>{title}</strong>
              <input
                style={{ height: "25px", width: "90%" }}
                className="form-control ms-2 mb-1 me-2"
                type="search"
                placeholder={`Search ${title}...`}
                aria-label={`Search ${title}`}
                value={searchValues[title] || ""}
                onChange={(e) => handleSearchChange(title, e.target.value)}
              />
              <hr className="m-0 p-0" />
              <div
                className="row suggestion mt-1"
                key={filteredItems.length}
                style={{
                  maxHeight: "250px",
                  minHeight: "50px",
                  overflowY: "auto",
                }}
              >
                {filteredItems.length > 0 ? (
                  filteredItems.map((item, index) => (
                    <div
                      key={index}
                      className="col-sx fade-in"
                      style={{ fontSize: "12px" }}
                    >
                      <label
                        className="ms-1 text-start"
                        key={item}
                        style={{ display: "block", cursor: "pointer" }}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedSet.has(item)}
                          onChange={() =>
                            toggleSelection(setFunc, selectedSet, item)
                          }
                        />
                        {item || "N/A"}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="col-md-12 text-center">No items found</div>
                )}
              </div>
              <hr className="m-0 p-0" />
              <div className="d-flex justify-content-evenly mt-1">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setFunc(new Set(filteredItems))}
                >
                  Select All
                </button>
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => setFunc(new Set())}
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="papaDiv">
      <div
        className="row"
        style={{
          border: "1px solid #ccc",
          background: "rgba(255, 255, 255, 0.22)",
          boxShadow: "5px 5px 20px rgba(7, 7, 7, 0.3)",
          backdropFilter: "blur(15px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
      >
        {renderCheckboxList(
          "Section",
          availableSection,
          selectedSection,
          setSelectedSection
        )}
        {renderCheckboxList(
          "Direction",
          availableDirection,
          selectedDirection,
          setSelectedDirection
        )}
        {renderCheckboxList(
          "Department",
          availableDepartment,
          selectedDepartment,
          setSelectedDepartment
        )}
        {renderCheckboxList(
          "Type",
          availableType,
          selectedType,
          setSelectedType
        )}
        {renderCheckboxList(
          "Block Type",
          availableBlockType,
          selectedBlockType,
          setSelectedBlockType
        )}
        {renderCheckboxList(
          "Granted/Not",
          availableGrantedNot,
          selectedGrantedNot,
          setSelectedGrantedNot
        )}
        {renderCheckboxList(
          "Availed/Not",
          availableAvailedNot,
          selectedAvailedNot,
          setSelectedAvailedNot
        )}
        {renderCheckboxList(
          "Burst",
          availableBurst,
          selectedBurst,
          setSelectedBurst
        )}
        {renderCheckboxList(
          "Corridore",
          availableCorridore,
          selectedCorridore,
          setSelectedCorridore
        )}
        {renderCheckboxList(
          "Rolling Block",
          availableRollingBlock,
          selectedRollingBlock,
          setSelectedRollingBlock
        )}
        <OverlayTrigger
          delay={{ show: 100, hide: 200 }}
          overlay={<Tooltip id="tooltip-reset">Reset Filter</Tooltip>}
        >
          <div
            className="btn btn-sm btn-danger d-flex justify-content-center align-items-center mt-1 mb-1"
            style={{
              maxWidth: "35px",
              boxShadow: "3px 3px 10px rgba(52, 80, 142, 0.5)",
            }}
            onClick={defaultSetup}
          >
            <RiResetLeftLine style={{ fontSize: "15px", fontWeight: "bold" }} />
          </div>
        </OverlayTrigger>
      </div>
    </div>
  );
};

export default UpdateFilters;
